import { type QueryKey, skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { chunk, isUndefined } from 'lodash';
import { useMemo } from 'react';

import { Assets } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getAdMetricSourceKey, type MetricSource } from '~/swr-hooks/ads/meta/useAdMetricSource';
import { type Ad } from '~/swr-hooks/ads/types';

export const AD_METRIC_SOURCES_KEY = 'AD_METRIC_SOURCES_KEY';

export type AdMetricSourcesKey = [typeof AD_METRIC_SOURCES_KEY, { adIds: Ad['id'][] }];
export const isAdMetricSourcesKey = (key: QueryKey): key is AdMetricSourcesKey => key[0] === AD_METRIC_SOURCES_KEY;

export type UseAdMetricSourcesParams = {
  adIds: Ad['id'][];
};

export const getAdMetricSourcesKey = ({ adIds }: UseAdMetricSourcesParams): AdMetricSourcesKey => [
  AD_METRIC_SOURCES_KEY,
  { adIds },
];

export type AdsMetricSourcesQueryCacheType = Record<Ad['id'], MetricSource | null | undefined>;

const BATCH_SIZE = 100;

export const useAdMetricSources = ({ adIds }: UseAdMetricSourcesParams) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const queryClient = useQueryClient();

  // sort ad ids to ensure consistent query key
  const sortedAdIds = useMemo(() => [...adIds].sort(), [adIds]);

  return useQuery<AdsMetricSourcesQueryCacheType>({
    queryKey: getAdMetricSourcesKey({ adIds: sortedAdIds }),
    queryFn:
      workspaceId && adIds.length > 0
        ? async () => {
            const result: Record<Ad['id'], MetricSource | null> = {};
            const missingAdIds: Ad['id'][] = [];

            adIds.forEach((adId) => {
              const cached = queryClient.getQueryData<MetricSource | null>(getAdMetricSourceKey({ adId }));

              if (!isUndefined(cached)) {
                result[adId] = cached;
              } else {
                missingAdIds.push(adId);
              }
            });

            if (missingAdIds.length > 0) {
              const missingAdIdsChunks = chunk(missingAdIds, BATCH_SIZE);
              for (const chunk of missingAdIdsChunks) {
                const metricSources = await callAirApi<MetricSource[]>(() =>
                  Assets.Versions.MetricSource.list({ sourceId: chunk, workspaceId }),
                );

                chunk.forEach((adId) => {
                  const metricSource = metricSources?.find((ms) => ms.sourceId === adId) ?? null;
                  result[adId] = metricSource;
                  queryClient.setQueryData<MetricSource | null>(getAdMetricSourceKey({ adId }), metricSource);
                });
              }
            }

            return result;
          }
        : skipToken,
    enabled: !!workspaceId && adIds.length > 0,
  });
};
